import { useEffect, useRef } from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

interface BottomSheetProps {
  open: boolean
  onClose: () => void
  /** Centered title rendered in the sheet header (Kalnia). Omit for a custom header. */
  title?: string
  children: React.ReactNode
  className?: string
  /**
   * Keep the panel mounted while closed (hidden off-screen) instead of unmounting it.
   * Lets a child that's expensive to mount — e.g. the Payrails hosted-field iframes —
   * load in the background and appear instantly on open. Uses `visibility` (not
   * `display:none`) so the hidden fields retain a layout box and measure correctly.
   */
  keepMounted?: boolean
  /**
   * Fires once the panel has finished sliding in (or via a timeout fallback when the
   * open animation is skipped, e.g. reduced-motion). Lets a child defer layout-sensitive
   * work — mounting the Payrails hosted-field iframes — until the panel is fully on-screen
   * and static, so their first layout isn't throttled by iOS WebKit while off-screen.
   */
  onOpened?: () => void
}

/** Modal bottom sheet: blurred backdrop + slide-up panel anchored to the bottom. */
function BottomSheet({
  open,
  onClose,
  title,
  children,
  className,
  keepMounted = false,
  onOpened,
}: BottomSheetProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose()
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [open, onClose])

  // Signal "fully opened" once per open cycle. The panel's slide-in `animationend` is
  // the precise trigger; the timeout is the fallback for when the animation is skipped.
  const openedFired = useRef(false)
  const fireOpened = () => {
    if (openedFired.current || !open) return
    openedFired.current = true
    onOpened?.()
  }
  useEffect(() => {
    if (!onOpened) return
    if (!open) {
      openedFired.current = false
      return
    }
    const t = setTimeout(fireOpened, 360)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onOpened])

  if (!open && !keepMounted) return null

  const hidden = !open

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col justify-end font-body",
        hidden && "pointer-events-none invisible",
      )}
      role="dialog"
      aria-modal="true"
      aria-hidden={hidden || undefined}
      inert={hidden}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-dark-base/80 backdrop-blur-[10px]",
          hidden ? "opacity-0" : "animate-in fade-in",
        )}
      />
      <div
        className={cn(
          "relative mx-auto max-h-[92dvh] w-full max-w-app overflow-y-auto rounded-t-3xl bg-dark-surface text-purple-light",
          hidden ? "translate-y-full" : "animate-in slide-in-from-bottom duration-300",
          className,
        )}
        onAnimationEnd={(e) => {
          // Only the panel's own slide-in, not animations bubbling from children.
          if (onOpened && e.target === e.currentTarget) fireOpened()
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center text-purple-light/70 transition-colors hover:text-purple-light"
        >
          <X className="size-6" />
        </button>
        {title && (
          <div className="flex h-14 items-center justify-center px-12">
            <h2 className="font-display text-[22px] font-medium text-purple-light">{title}</h2>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

export { BottomSheet }
